/**
 * Troubleshooting Hooks
 *
 * React hooks for tracking events in components
 */

import { useCallback, useEffect, useRef } from 'react';

import { getTroubleshootingTracker } from './TroubleshootingTracker';
import {
  type DataProviderTrackingData,
  type DataProviderTrackingDataPartial,
  LogLevel,
  TrackingEventType,
  TrackingFeature,
} from './types';

/**
 * Hook for tracking data provider events (HTTP/SQL API calls)
 * Supports cascading, pagination, and initial load scenarios
 */
// eslint-disable-next-line import/prefer-default-export
export function useDataProviderTracking() {
  const tracker = getTroubleshootingTracker();
  const apiStartTimeRef = useRef<Map<string, number>>(new Map());
  const isMountedRef = useRef(true);
  const activeApiCallsRef = useRef<Map<string, number>>(new Map()); // fieldId -> count

  // Track component unmount
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  /**
   * Track when a value is selected in a field with data provider
   */
  const trackValueSelected = useCallback(
    (data: {
      fieldId: string;
      fieldName: string;
      selectedValue: unknown;
      dependencies?: Record<string, unknown>;
    }) => {
      if (!tracker.isEnabled()) {
        return '';
      }

      try {
        return tracker.track(
          TrackingFeature.DATA_PROVIDER,
          TrackingEventType.DATA_PROVIDER_VALUE_SELECTED,
          {
            fieldId: data.fieldId,
            fieldName: data.fieldName,
            selectedValue: data.selectedValue,
            dependencies: data.dependencies,
          },
          undefined,
          undefined,
          LogLevel.INFO
        );
      } catch (error) {
        // Silently fail
        return '';
      }
    },
    [tracker]
  );

  /**
   * Track when a data provider API is triggered
   */
  const trackApiTriggered = useCallback(
    (data: DataProviderTrackingData): string => {
      if (!tracker.isEnabled()) {
        return '';
      }

      try {
        const now = Date.now();
        const { fieldId } = data;

        // Concurrent request detection
        const activeCount = activeApiCallsRef.current.get(fieldId) || 0;
        const isConcurrent = activeCount > 0;

        // Update active calls count
        activeApiCallsRef.current.set(fieldId, activeCount + 1);

        // Build metadata
        const metadata: Record<string, unknown> = {};

        // Prepare tracking data with start time
        const trackingData: DataProviderTrackingData = {
          ...data,
          startTime: now,
          status: 'pending',
          isConcurrent,
          metadata: {
            ...data.metadata,
            ...metadata,
          },
        };

        // Convert to Record<string, unknown> for tracker.track()
        // This is safe because DataProviderTrackingData is compatible
        const eventData: Record<string, unknown> = {
          ...trackingData,
          triggerType: trackingData.triggerType,
          dataProviderType: trackingData.dataProviderType,
        };

        const eventId = tracker.track(
          TrackingFeature.DATA_PROVIDER,
          TrackingEventType.DATA_PROVIDER_API_TRIGGERED,
          eventData,
          undefined,
          metadata,
          LogLevel.INFO
        );

        // Store start time for this API call
        if (eventId) {
          apiStartTimeRef.current.set(eventId, now);
        }

        return eventId;
      } catch (error) {
        // Silently fail
        return '';
      }
    },
    [tracker]
  );

  /**
   * Track successful API response
   */
  const trackApiSuccess = useCallback(
    (
      eventId: string,
      data: DataProviderTrackingDataPartial,
      duration?: number
    ) => {
      if (!tracker.isEnabled() || !eventId) {
        return;
      }

      try {
        // Check if component is still mounted
        if (!isMountedRef.current) {
          // Component unmounted during API call
          const startTime = apiStartTimeRef.current.get(eventId);
          const unmountDuration = startTime
            ? Date.now() - startTime
            : undefined;

          tracker.track(
            TrackingFeature.DATA_PROVIDER,
            TrackingEventType.DATA_PROVIDER_API_ERROR,
            {
              fieldId: data.fieldId,
              fieldName: data.fieldName,
              error: {
                type: 'unknown',
                message: 'Component unmounted during API call',
              },
              status: 'cancelled',
            },
            unmountDuration,
            {
              originalEventId: eventId,
              componentUnmounted: true,
            },
            LogLevel.WARN,
            eventId
          );

          // Clean up
          apiStartTimeRef.current.delete(eventId);

          return;
        }

        // Use provided duration or calculate from start time
        let calculatedDuration = duration;

        if (calculatedDuration === undefined) {
          const startTime = apiStartTimeRef.current.get(eventId);

          calculatedDuration = startTime ? Date.now() - startTime : undefined;
        }

        const trackingData = {
          ...data,
          status: 'success',
        };

        // Convert to Record<string, unknown> for tracker.track()
        const eventData: Record<string, unknown> = {
          ...trackingData,
        };

        tracker.track(
          TrackingFeature.DATA_PROVIDER,
          TrackingEventType.DATA_PROVIDER_API_SUCCESS,
          eventData,
          calculatedDuration,
          {
            originalEventId: eventId,
          },
          LogLevel.INFO,
          eventId // correlationId
        );

        // Clean up
        apiStartTimeRef.current.delete(eventId);
        const { fieldId: successFieldId } = data;

        const successActiveCount =
          activeApiCallsRef.current.get(successFieldId) || 0;

        if (successActiveCount > 0) {
          activeApiCallsRef.current.set(successFieldId, successActiveCount - 1);
        }
      } catch (error) {
        // Silently fail
      }
    },
    [tracker]
  );

  /**
   * Track API error
   */
  const trackApiError = useCallback(
    (
      eventId: string,
      data: DataProviderTrackingDataPartial,
      duration?: number
    ) => {
      if (!tracker.isEnabled() || !eventId) {
        return;
      }

      try {
        // Use provided duration or calculate from start time
        let calculatedDuration = duration;

        if (calculatedDuration === undefined) {
          const startTime = apiStartTimeRef.current.get(eventId);

          calculatedDuration = startTime ? Date.now() - startTime : undefined;
        }

        const trackingData = {
          ...data,
          status: 'error',
        };

        // Convert to Record<string, unknown> for tracker.track()
        const eventData: Record<string, unknown> = {
          ...trackingData,
        };

        tracker.track(
          TrackingFeature.DATA_PROVIDER,
          TrackingEventType.DATA_PROVIDER_API_ERROR,
          eventData,
          calculatedDuration,
          {
            originalEventId: eventId,
          },
          LogLevel.ERROR,
          eventId // correlationId
        );

        // Clean up
        apiStartTimeRef.current.delete(eventId);
        const { fieldId: errorFieldId } = data;

        const errorActiveCount =
          activeApiCallsRef.current.get(errorFieldId) || 0;

        if (errorActiveCount > 0) {
          activeApiCallsRef.current.set(errorFieldId, errorActiveCount - 1);
        }
      } catch (error) {
        // Silently fail
      }
    },
    [tracker]
  );

  return {
    trackValueSelected,
    trackApiTriggered,
    trackApiSuccess,
    trackApiError,
  };
}
